import { useEffect, useMemo, useState } from 'react';
import { getPageBySlug } from '../api/pagesApi';
import { getSectionsByPageId } from '../api/sectionsApi';
import SectionEditor from '../features/cms/sections/SectionEditor';

const SECTION_LABELS = {
  navbar: 'Navbar',
  'navbar-mega-solutions': 'Solutions',
  'navbar-mega-markets': 'Markets',
  'navbar-mega-wealth': 'Wealth & Savings',
  'navbar-mega-technology': 'Technology',
  'navbar-mega-insights': 'Insights',
  footer: 'Footer',
  'footer-column-solutions': 'Solutions',
  'footer-column-company': 'Company',
  'footer-column-legal': 'Legal',
};

const getSectionLabel = (section) => {
  if (SECTION_LABELS[section.key]) {
    return SECTION_LABELS[section.key];
  }

  if (section.key.startsWith('navbar-mega-')) {
    return section.name.replace('Navbar Mega Menu - ', '');
  }

  if (section.key.startsWith('footer-column-')) {
    return section.name.replace('Footer Column - ', '');
  }

  return section.name;
};

const GlobalLayoutPage = () => {
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSections = async (keepSelection = true, options = {}) => {
    const { silent = false } = options;

    if (!silent) {
      setIsLoading(true);
    }
    setError('');

    try {
      const page = await getPageBySlug('global-layout');
      if (!page) {
        throw new Error('Global layout page was not found.');
      }

      const loadedSections = await getSectionsByPageId(page.id);
      setSections(loadedSections);

      if (keepSelection && selectedSectionId) {
        const stillExists = loadedSections.some((section) => section.id === selectedSectionId);
        if (!stillExists && loadedSections.length > 0) {
          setSelectedSectionId(loadedSections[0].id);
        }
      } else if (loadedSections.length > 0) {
        setSelectedSectionId(loadedSections[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load global layout.');
      setSections([]);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSections(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { navbarSection, megaMenuSections, footerSection, footerColumnSections } = useMemo(() => {
    const navbar = sections.find((section) => section.key === 'navbar');
    const megaMenus = sections.filter((section) => section.key.startsWith('navbar-mega-'));
    const footer = sections.find((section) => section.key === 'footer');
    const footerColumns = sections.filter((section) => section.key.startsWith('footer-column-'));
    return {
      navbarSection: navbar,
      megaMenuSections: megaMenus,
      footerSection: footer,
      footerColumnSections: footerColumns,
    };
  }, [sections]);

  const selectedSection = sections.find((section) => section.id === selectedSectionId);

  const renderSectionButton = (section, showKey = false) => {
    const isActive = selectedSectionId === section.id;

    return (
      <li key={section.id}>
        <button
          type="button"
          onClick={() => setSelectedSectionId(section.id)}
          className={`w-full rounded-md border px-3 py-2.5 text-left transition !shadow-none hover:!translate-y-0 hover:!shadow-none ${
            isActive
              ? '!border-[#d4d4d8] !bg-[#f4f4f5] font-medium !text-[#130705]'
              : '!border-transparent !bg-white !text-[#4a4340] hover:!border-[#e4e4e7] hover:!bg-[#fafafa] hover:!text-[#130705]'
          }`}
        >
          <span className="block text-[13px]">{getSectionLabel(section)}</span>
          {showKey ? (
            <span className="mt-0.5 block text-[11px] text-[#a8a098]">{section.key}</span>
          ) : null}
        </button>
      </li>
    );
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading global layout…</p>;
  }

  if (error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        {error}
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Global layout
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Navbar, mega menus &amp; footer
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Manage the public navbar and footer in focused sections. Logo, social links,
          column links, and text can each be edited separately.
        </p>
      </header>

      <div className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <div className="rounded-md border border-[#e4d9c8] bg-white p-3">
            <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
              Navbar
            </p>
            <ul className="space-y-1">
              {navbarSection ? renderSectionButton(navbarSection, true) : null}
            </ul>
          </div>

          <div className="rounded-md border border-[#e4d9c8] bg-white p-3">
            <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
              Mega menus
            </p>
            <ul className="space-y-1">
              {megaMenuSections.map((section) => renderSectionButton(section))}
            </ul>
          </div>

          <div className="rounded-md border border-[#e4d9c8] bg-white p-3">
            <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
              Footer
            </p>
            <ul className="space-y-1">
              {footerSection ? renderSectionButton(footerSection, true) : null}
              {footerColumnSections.map((section) => renderSectionButton(section))}
            </ul>
          </div>
        </aside>

        <div className="min-w-0 rounded-md border border-[#e4d9c8] bg-white p-5 sm:p-6">
          {selectedSection ? (
            <SectionEditor
              section={selectedSection}
              onSaved={() => loadSections(true, { silent: true })}
            />
          ) : (
            <p className="text-sm text-[#6b635c]">Select a section to edit.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalLayoutPage;
